import { useEffect, useMemo, useState } from "react";
import type {
  TrainingEvalBaselineKey,
  TrainingEvalBaselineView,
  TrainingEvalRunSummaryView,
  TrainingEvalRunView,
  TrainingEvalScenarioView,
  TrainingEvalSuccessTermsView,
} from "@/api/trainingEval";
import { isHttpClientError } from "@/http";
import { mapWithConcurrency } from "@/utils/concurrency";
import {
  BASELINE_FALLBACK_CONFIG,
  baselineKeyOf,
  baselineKeyNotice,
  baselineNotices,
  buildBaselineSeries,
  type EvalBaselineState,
} from "./evalBaselineModel";
import { loadEvalBaseline, loadEvalRun, loadEvalRunSuccessTerms } from "./evalRunDataCache";

// 브라우저의 HTTP/1.1 origin 연결 한도와 같은 값이다. 이보다 크게 하면 대기열만 길어진다.
const BASELINE_LOAD_CONCURRENCY = 6;

type BaselineLookup = {
  response: TrainingEvalBaselineView;
  // 대신 조회한 config 다. 선택한 config 의 결과를 쓰면 null.
  fallbackConfig: string | null;
};
type BaselineData = BaselineLookup & {
  details: Map<string, TrainingEvalRunView>;
  terms: Map<string, TrainingEvalSuccessTermsView>;
};

// 선택한 config 결과가 없으면 같은 Dataset·TASK 의 default config 로 한 번 더 조회한다. 대체 조회는
// 부가 기능이다. 그 조회가 실패하면 선택한 config 의 빈 결과를 그대로 쓴다. 키마다 캐시하므로 여러
// config 가 같은 default 결과를 함께 쓴다.
async function loadBaselineWithFallback(
  key: TrainingEvalBaselineKey,
  configMissing: boolean,
): Promise<BaselineLookup> {
  const response = await loadEvalBaseline(key);
  // config 이름이 없는 run 은 처음부터 default config 로 조회한다. 그 결과도 대체 결과로 표시한다.
  if (configMissing)
    return {
      response,
      fallbackConfig: response.session === null ? null : BASELINE_FALLBACK_CONFIG,
    };
  if (response.session !== null || key.configName === BASELINE_FALLBACK_CONFIG)
    return { response, fallbackConfig: null };
  const fallback = await loadEvalBaseline({ ...key, configName: BASELINE_FALLBACK_CONFIG }).catch(
    () => null,
  );
  return fallback !== null && fallback.session !== null
    ? { response: fallback, fallbackConfig: BASELINE_FALLBACK_CONFIG }
    : { response, fallbackConfig: null };
}

type BaselineLoad =
  | { key: string; status: "loading" | "error" | "unavailable" }
  | { key: string; status: "success"; data: BaselineData };

export function useEvalBaseline(
  summaries: readonly TrainingEvalRunSummaryView[],
  scenarios: readonly TrainingEvalScenarioView[],
  enabled: boolean,
  refreshKey = 0,
): EvalBaselineState {
  // 요청이 끝날 때마다 컨트롤러를 다시 그린다. run 이 수백 개면 이 판정만 수백 번 실행된다.
  const match = useMemo(() => baselineKeyOf(summaries), [summaries]);
  const configName = match.key?.configName;
  const datasetUuid = match.key?.datasetUuid;
  const task = match.key?.task;
  const configMissing = match.key === null ? false : match.configMissing;
  const key = JSON.stringify([configName, datasetUuid, task, configMissing]);
  const [load, setLoad] = useState<BaselineLoad | null>(null);
  useEffect(() => {
    if (!enabled || configName === undefined || datasetUuid === undefined || task === undefined)
      return;
    let disposed = false;
    setLoad({ key, status: "loading" });
    void loadBaselineWithFallback({ configName, datasetUuid, task }, configMissing)
      .then(async ({ response, fallbackConfig }) => {
        if (disposed) return;
        const details = new Map<string, TrainingEvalRunView>();
        const terms = new Map<string, TrainingEvalSuccessTermsView>();
        // detail 과 terms 는 같은 단계에서 시작한다. 개별 실패는 기존 merge 제외 규칙이 처리한다.
        // 사용자가 고르지 않은 Seed 수만큼 요청이 늘어난다. 그래서 상한을 둔다(rules.md §7).
        // 상한은 baseline 요청이 사용자가 보고 있는 열의 응답을 늦추지 않게 한다.
        await mapWithConcurrency(
          response.runs.flatMap((run) => [
            async () => {
              details.set(run.id, await loadEvalRun(run.id));
            },
            async () => {
              terms.set(run.id, await loadEvalRunSuccessTerms(run.id));
            },
          ]),
          (load) => load().catch(() => undefined),
          BASELINE_LOAD_CONCURRENCY,
        );
        if (!disposed)
          setLoad({ key, status: "success", data: { response, fallbackConfig, details, terms } });
      })
      .catch((error: unknown) => {
        if (!disposed)
          setLoad({
            key,
            // @compat MLP-829 until=be:4399f2f1 partial
            status: isHttpClientError(error) && error.status === 404 ? "unavailable" : "error",
          });
      });
    return () => {
      disposed = true;
    };
  }, [enabled, configName, datasetUuid, task, configMissing, key, refreshKey]);

  return useMemo(() => {
    if (match.reason !== null)
      return { status: "idle", series: null, notices: baselineKeyNotice(match.reason) };
    if (load?.key !== key)
      return { status: enabled ? "loading" : "idle", series: null, notices: [] };
    if (load.status === "success") {
      const { response, fallbackConfig, details, terms } = load.data;
      const result = buildBaselineSeries(response, details, terms, scenarios, fallbackConfig);
      return {
        status: "success",
        series: result.series,
        notices: baselineNotices(response, result, configMissing),
      };
    }
    if (load.status === "error")
      return {
        status: "error",
        series: null,
        notices: [
          {
            key: "baseline-error",
            tone: "warning",
            message: "Baseline을 불러오지 못했습니다. 창을 다시 열어 시도해 주세요.",
          },
        ],
      };
    return { status: load.status === "loading" ? "loading" : "idle", series: null, notices: [] };
  }, [match.reason, load, key, enabled, scenarios, configMissing]);
}
